import React, { useState, useEffect } from 'react'
import { Modal, Button, Select, Table, Tag, Row, Col, Card, Collapse, Spin, message, Statistic, Space, FloatButton, Empty, Alert } from 'antd'
import { SearchOutlined, PrinterOutlined, CopyOutlined, MailOutlined, CheckCircleOutlined, CloseCircleOutlined, RollbackOutlined, SendOutlined, BarChartOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import ReactECharts from 'echarts-for-react'
import { baseUrlAREA, HEADERS_BASIC, LOAD_REPORT_LETTER } from '../../../API/apiUrlsNew'
import { getStatusColor } from '../hook/function'
import { ThaiMonthPicker } from '../../../../hook/ThaiMonthPicker'
import { printReportLetter } from './printReportLetter'

const ALL_TYPES = ['บอกกล่าว', 'บอกกล่าว(มือ)', 'สิทธิซื้อรถคืน', 'สิทธิซื้อรถคืน(เก่า)', 'แจ้งราคาขายรถ', 'บอกเลิก', 'บอกเลิก(มือ)', 'พิพากษา', 'แจ้งผลประมูล']
// สถานะตอบกลับ (status4) สำหรับ Select กรอง
const STATUS4_OPTIONS = ['ตอบกลับ', 'ตีกลับ', 'ส่งถึงแล้วไม่ตอบกลับ', 'ไม่มีการเคลื่อนไหว']
// สถานะคอนเฟิร์ม (status3) สำหรับ Select กรองขั้น 3
const STATUS3_OPTIONS = ['รอคอนเฟิร์ม', 'คอนเฟิร์มแล้ว']
const chunkArray = (arr, size) => Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size))

function ReportLetter({ open, close }) {
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState([])
    const [summary, setSummary] = useState([])
    const [selectedMonth, setSelectedMonth] = useState(null)
    const [filterType, setFilterType] = useState('ทั้งหมด')
    const [filterStatus4, setFilterStatus4] = useState('ทั้งหมด')
    const [filterStatus3, setFilterStatus3] = useState('ทั้งหมด')
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [emsGo, setEmsGo] = useState([])
    const [emsBack, setEmsBack] = useState([])
    const [searched, setSearched] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const userCompany = localStorage.getItem("COMPANY_ID");

    const loadReport = () => {
        if (!selectedMonth) {
            message.warning('กรุณาเลือกเดือน')
            return
        }
        setLoading(true)
        setErrorMsg('')
        const payload = {
            date: selectedMonth.format('YYYY-MM'),
            company: userCompany
        }
        axios.post(baseUrlAREA + LOAD_REPORT_LETTER, payload, { headers: HEADERS_BASIC })
            .then(res => {
                if (res.status === 200) {
                    setData((res.data.data || []).map((d, i) => ({ ...d, _rowKey: String(i) })))
                    setSummary(res.data.summary || [])
                    setFilterType('ทั้งหมด')
                    setFilterStatus4('ทั้งหมด')
                    setFilterStatus3('ทั้งหมด')
                    setSelectedRowKeys([])
                    setEmsGo([])
                    setEmsBack([])
                }
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err?.message || 'ไม่สามารถโหลดข้อมูลได้'
                setErrorMsg(msg)
                setData([])
                setSummary([])
                message.error(msg)
            })
            .finally(() => {
                setSearched(true)
                setLoading(false)
            })
    }

    // console.log("data", data)

    const typeOptions = ['ทั้งหมด', ...ALL_TYPES]
    // กรองพร้อมกันทั้งประเภทจดหมาย (status1) และสถานะตอบกลับ (status4)
    const applyFilters = (rows) => rows.filter(d =>
        (filterType === 'ทั้งหมด' || d.status1 === filterType) &&
        (filterStatus4 === 'ทั้งหมด' || d.status4 === filterStatus4) &&
        (filterStatus3 === 'ทั้งหมด' || d.status3 === filterStatus3)
    )
    const displayData = applyFilters(data)
    // ค้นหาแบบขั้นบันได:
    // ระดับ 2 (สถานะ/status4) นับจากข้อมูลที่กรองด้วยประเภทจดหมาย (ระดับ 1) แล้ว
    const dataByType = filterType === 'ทั้งหมด' ? data : data.filter(d => d.status1 === filterType)
    // ระดับ 3 (คอนเฟิร์ม/status3) นับจากข้อมูลที่กรองด้วยระดับ 1 + ระดับ 2 แล้ว
    const dataByTypeStatus4 = filterStatus4 === 'ทั้งหมด' ? dataByType : dataByType.filter(d => d.status4 === filterStatus4)

    const getSummaryCount = (status) => summary.find(s => s.status === status)?.totalCount ?? 0
    const confirmed = getSummaryCount('คอนเฟิร์มแล้ว')
    const unconfirmed = getSummaryCount('รอคอนเฟิร์ม')
    const replied = getSummaryCount('ตอบกลับ')
    const returned = getSummaryCount('ตีกลับ')
    const noMovement = getSummaryCount('ส่งถึงแล้วไม่ตอบกลับ') + getSummaryCount('ไม่มีการเคลื่อนไหว')

    const dateLabel = selectedMonth
        ? selectedMonth.locale('th').format('MMMM') + ' ' + (selectedMonth.year() + 543)
        : ''

    // ── Charts ──────────────────────────────────────────────────────
    const STATUS1_COLOR_MAP = {
        'บอกกล่าว': '#13c2c2',
        'บอกกล่าว(มือ)': '#13c2c2',
        'สิทธิซื้อรถคืน': '#722ed1',
        'สิทธิซื้อรถคืน(เก่า)': '#722ed1',
        'แจ้งราคาขายรถ': '#fa8c16',
        'บอกเลิก': '#eb2f96',
        'บอกเลิก(มือ)': '#eb2f96',
        'พิพากษา': '#8c8c8c',
        'แจ้งผลประมูล': '#722ed1',
    }

    const barByType = (() => {
        // ใช้ summary จาก server — กรองเฉพาะ ALL_TYPES แล้วต่อด้วย status ที่เหลือใน summary
        const summaryTypes = summary.filter(s => s.status !== 'ทั้งหมด' && ALL_TYPES.includes(s.status) && s.totalCount > 0)
        const extra = summary.filter(s => s.status !== 'ทั้งหมด' && !ALL_TYPES.includes(s.status) && s.totalCount > 0 &&
            !['อยู่ระหว่างการนำจ่าย', 'นำจ่ายไม่สำเร็จ', 'นำจ่ายสำเร็จ', 'รอคอนเฟิร์ม', 'คอนเฟิร์มแล้ว', 'ส่งถึงแล้วไม่ตอบกลับ', 'ไม่มีการเคลื่อนไหว', 'ตอบกลับ', 'ตีกลับ'].includes(s.status))
        const items = [...summaryTypes, ...extra]
        if (items.length === 0) return null
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: '2%', right: '2%', bottom: '30%', top: '10%', containLabel: true },
            xAxis: {
                type: 'category',
                data: items.map(s => s.nameStatus),
                axisLabel: { interval: 0, rotate: 40, fontSize: 10, color: '#595959' },
            },
            yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } },
            series: [{
                name: 'จำนวน',
                type: 'bar',
                barWidth: '45%',
                data: items.map(s => ({
                    value: s.totalCount,
                    itemStyle: { color: STATUS1_COLOR_MAP[s.status] || '#8c8c8c', borderRadius: [4, 4, 0, 0] },
                })),
                label: { show: true, position: 'top', color: '#595959', fontWeight: 'bold', formatter: p => p.value > 0 ? p.value : '' },
            }],
        }
    })()

    const pieConfirm = ({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: '0%', left: 'center' },
        series: [{
            name: 'สถานะคอนเฟิร์ม',
            type: 'pie',
            radius: ['35%', '55%'],
            center: ['50%', '42%'],
            itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
            label: { show: true, formatter: '{b}\n{c}', fontWeight: 'bold', fontSize: 12 },
            data: [
                { value: confirmed, name: 'คอนเฟิร์มแล้ว', itemStyle: { color: '#52c41a' } },
                { value: unconfirmed, name: 'รอคอนเฟิร์ม', itemStyle: { color: '#faad14' } },
            ],
        }],
    })

    // ── Print ────────────────────────────────────────────────────────
    // โค้ดพิมพ์ถูกแยกไปที่ ./printReportLetter.js (คงรูปแบบเดิม)
    // ข้อมูลที่พิมพ์ = แถวที่ติ๊กเลือก (ถ้ามี) มิฉะนั้นใช้ข้อมูลที่กรองด้วย select
    // ถ้าไม่กรองและไม่ติ๊ก => เป็นข้อมูลทั้งหมด
    const handlePrint = () => {
        const keySet = new Set(selectedRowKeys.map(String))
        const printData = selectedRowKeys.length > 0
            ? displayData.filter(d => keySet.has(d._rowKey))
            : displayData
        printReportLetter({ displayData: printData, dateLabel, filterType })
    }

    useEffect(() => {
        if (selectedRowKeys.length === 0) {
            setEmsGo([])
            setEmsBack([])
            return
        }
        const keySet = new Set(selectedRowKeys.map(String))
        const filtered = data.filter(d =>
            (filterType === 'ทั้งหมด' || d.status1 === filterType) &&
            (filterStatus4 === 'ทั้งหมด' || d.status4 === filterStatus4) &&
            (filterStatus3 === 'ทั้งหมด' || d.status3 === filterStatus3)
        )
        const picked = filtered.filter(d => keySet.has(d._rowKey))
        setEmsGo(picked.map(d => d.parcelNo).filter(Boolean))
        setEmsBack(picked.map(d => d.parcelNoResponse).filter(Boolean))
    }, [selectedRowKeys, data, filterType, filterStatus4, filterStatus3])

    const emsGoChunks = chunkArray(emsGo, 300)
    const emsBackChunks = chunkArray(emsBack, 300)

    // ── Copy EMS ─────────────────────────────────────────────────────
    const handleCopyEms = (arr, label) => {
        if (arr.length === 0) { message.warning(`ไม่มีเลข ${label} ในรายการที่เลือก`); return }
        navigator.clipboard.writeText(arr.join('\n'))
            .then(() => message.success(`คัดลอก ${label} สำเร็จ (${arr.length} เลข)`))
            .catch(() => message.error('คัดลอกไม่สำเร็จ'))
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: keys => setSelectedRowKeys(keys),
    }

    const columns = [
        { title: 'ลำดับ', key: 'index', align: 'center', width: 55, render: (_, __, i) => i + 1 },
        { title: 'เลขสัญญา', dataIndex: 'CONTNO', key: 'CONTNO', align: 'center', width: 120 },
        {
            title: 'ชื่อลูกค้า', dataIndex: 'customerFullName', key: 'customerFullName',
            render: (v, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{v || '-'}</span>
                    {record.customerTypeId === 0
                        ? <Tag color="blue" style={{ margin: 0 }}>ผู้เช่าซื้อ</Tag>
                        : <Tag color="orange" style={{ margin: 0 }}>ผู้ค้ำประกัน {record.customerTypeId}</Tag>}
                </div>
            ),
        },
        { title: 'EMS ไป', dataIndex: 'parcelNo', key: 'parcelNo', align: 'center', width: 130, render: v => v || '-' },
        { title: 'EMS กลับ', dataIndex: 'parcelNoResponse', key: 'parcelNoResponse', align: 'center', width: 130, render: v => v || '-' },
        { title: 'รอบที่ออกจดหมาย', dataIndex: 'roundDate', key: 'roundDate', align: 'center', width: 130, render: v => v ? dayjs(v).format('DD-MM-YYYY') : '-' },
        { title: 'วันที่จัดเก็บ', dataIndex: 'dateConfirm', key: 'dateConfirm', align: 'center', width: 110, render: v => v && !v?.startsWith('0001') ? dayjs(v).format('DD/MM/YYYY') : '-' },
        { title: 'ประเภท', dataIndex: 'status1', key: 'status1', align: 'center', width: 110, render: v => v ? <Tag color={getStatusColor(v)}>{v}</Tag> : '-' },
        { title: 'คอนเฟิร์ม', dataIndex: 'status3', key: 'status3', align: 'center', width: 120, render: v => v ? <Tag color={getStatusColor(v)}>{v}</Tag> : '-' },
        { title: 'ตอบกลับ', dataIndex: 'status4', key: 'status4', align: 'center', width: 170, render: v => v ? <Tag color={getStatusColor(v)}>{v}</Tag> : '-' },
    ]

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, background: '#fff3cc', border: '1px solid #ffcb05' }}>
                        <BarChartOutlined style={{ color: '#efa420' }} />
                        <span style={{ color: '#efa420', fontWeight: 'bold' }}>รายงานสรุปจดหมาย</span>
                    </div>
                </div>
            }
            open={open}
            onCancel={() => close(false)}
            footer={null}
            width="95%"
            style={{ top: 16 }}
            styles={{ body: { background: '#f0f2f5', padding: 16 } }}
        >
            <Spin spinning={loading}>
                {/* Header Controls */}
                <Card style={{ borderRadius: 12, marginBottom: 16, border: '1px solid #ffcb05', background: '#fffdf0' }}>
                    <Row gutter={8} align="middle">
                        <Col>
                            <ThaiMonthPicker value={selectedMonth} onChange={setSelectedMonth} size="large" />
                        </Col>
                        <Col>
                            <Button type="primary" icon={<SearchOutlined />} size="large" onClick={loadReport}
                                style={{ background: '#efa420', borderColor: '#efa420' }}>
                                โหลดข้อมูล
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {errorMsg && (
                    <Alert
                        type="error"
                        showIcon
                        message="เกิดข้อผิดพลาด"
                        description={errorMsg}
                        style={{ borderRadius: 12, marginBottom: 16 }}
                    />
                )}

                {!errorMsg && searched && data.length === 0 && (
                    <Card style={{ borderRadius: 12 }}>
                        <Empty description="ไม่พบข้อมูล" />
                    </Card>
                )}

                {!errorMsg && data.length > 0 && (
                    <>
                        {/* Summary + Charts (ยุบ/เปิดได้ — เริ่มต้นหุบไว้) */}
                        <Collapse
                            style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #ffcb05', background: '#fffdf0', overflow: 'hidden' }}
                            expandIconPosition="end"
                            items={[{
                                key: 'summary',
                                label: (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 'bold', fontSize: 17, color: '#efa420' }}>
                                        <BarChartOutlined style={{ fontSize: 20 }} />
                                        สรุปสถานะ &amp; กราฟ
                                    </span>
                                ),
                                style: { background: '#fff3cc', borderRadius: 12 },
                                children: (
                                    <>
                                        {/* Summary Cards */}
                                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                                            {[
                                                { title: 'ทั้งหมด', value: displayData.length, color: '#1677ff', icon: <MailOutlined style={{ color: '#1677ff' }} /> },
                                                { title: 'คอนเฟิร์มแล้ว', value: confirmed, color: '#52c41a', icon: <CheckCircleOutlined style={{ color: '#52c41a' }} /> },
                                                { title: 'รอคอนเฟิร์ม', value: unconfirmed, color: '#faad14', icon: <SendOutlined style={{ color: '#faad14' }} /> },
                                                { title: 'ตอบกลับ', value: replied, color: '#52c41a', icon: <CheckCircleOutlined style={{ color: '#52c41a' }} /> },
                                                { title: 'ตีกลับ', value: returned, color: '#ff4d4f', icon: <RollbackOutlined style={{ color: '#ff4d4f' }} /> },
                                                { title: 'ไม่มีการเคลื่อนไหว', value: noMovement, color: '#8c8c8c', icon: <CloseCircleOutlined style={{ color: '#8c8c8c' }} /> },
                                            ].map((s, i) => (
                                                <Col key={i} xs={12} sm={8} md={4} style={{ display: 'flex' }}>
                                                    <Card size="small" style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                        <Statistic title={s.title} value={s.value} prefix={s.icon}
                                                            valueStyle={{ color: s.color, fontWeight: 600, fontSize: 22 }} />
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Charts */}
                                        <Row gutter={[12, 12]}>
                                            <Col xs={24} lg={16}>
                                                <Card title={<span style={{ color: '#595959' }}>📊 จำนวนจดหมายแยกตามประเภท</span>}
                                                    style={{ borderRadius: 12, height: '100%' }}>
                                                    {barByType
                                                        ? <ReactECharts option={barByType} style={{ height: 300 }} />
                                                        : <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>ไม่มีข้อมูล</div>
                                                    }
                                                </Card>
                                            </Col>
                                            <Col xs={24} lg={8}>
                                                <Card title={<span style={{ color: '#595959' }}>✅ สัดส่วนสถานะคอนเฟิร์ม</span>}
                                                    style={{ borderRadius: 12, height: '100%' }}>
                                                    <ReactECharts option={pieConfirm} style={{ height: 300 }} />
                                                </Card>
                                            </Col>
                                        </Row>
                                    </>
                                ),
                            }]}
                        />

                        {/* EMS Copy */}
                        <Card size="small" style={{ borderRadius: 12, marginBottom: 16, background: '#fffdf0', border: '1px solid #ffcb05' }}>
                            <Row align="middle" gutter={[8, 8]}>
                                <Col>
                                    <span style={{ fontWeight: 'bold', color: '#efa420' }}>
                                        <CopyOutlined style={{ marginRight: 6 }} />คัดลอกเลข EMS จากรายการที่เลือก
                                    </span>
                                    <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                                        (เลือกแล้ว {selectedRowKeys.length} รายการ)
                                    </span>
                                    {selectedRowKeys.length > 0 && (
                                        <Button size="small" style={{ marginLeft: 8 }} onClick={() => setSelectedRowKeys([])}>
                                            ล้างการเลือก
                                        </Button>
                                    )}
                                </Col>
                                <Col style={{ marginLeft: 'auto' }}>
                                    <Space wrap>
                                        <span style={{ color: '#555', fontSize: 13 }}>EMS ไป ({emsGo.length}):</span>
                                        {emsGo.length === 0
                                            ? <Button size="small" icon={<CopyOutlined />} disabled>ไม่มีข้อมูล</Button>
                                            : emsGoChunks.map((chunk, i) => (
                                                <Button key={`go-${i}`} size="small" icon={<CopyOutlined />} style={{ marginRight: 4 }}
                                                    onClick={() => handleCopyEms(chunk, `EMS ไป ชุดที่ ${i + 1}`)}>
                                                    {emsGoChunks.length > 1 ? `ชุดที่ ${i + 1} (${chunk.length})` : `${chunk.length} เลข`}
                                                </Button>
                                            ))
                                        }
                                        <span style={{ color: '#555', fontSize: 13, marginLeft: 8 }}>EMS กลับ ({emsBack.length}):</span>
                                        {emsBack.length === 0
                                            ? <Button size="small" icon={<CopyOutlined />} disabled>ไม่มีข้อมูล</Button>
                                            : emsBackChunks.map((chunk, i) => (
                                                <Button key={`back-${i}`} size="small" icon={<CopyOutlined />} style={{ marginRight: 4 }}
                                                    onClick={() => handleCopyEms(chunk, `EMS กลับ ชุดที่ ${i + 1}`)}>
                                                    {emsBackChunks.length > 1 ? `ชุดที่ ${i + 1} (${chunk.length})` : `${chunk.length} เลข`}
                                                </Button>
                                            ))
                                        }
                                    </Space>
                                </Col>
                            </Row>
                        </Card>

                        {/* Filter Selects */}
                        <Card style={{ borderRadius: 12, marginBottom: 12 }}>
                            <Row gutter={[12, 12]} align="middle">
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ marginBottom: 4, fontWeight: 'bold', color: '#595959' }}>ประเภทจดหมาย</div>
                                    <Select
                                        value={filterType}
                                        onChange={(v) => { setFilterType(v); setFilterStatus4('ทั้งหมด'); setFilterStatus3('ทั้งหมด'); setSelectedRowKeys([]) }}
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="label"
                                        options={typeOptions.map(t => ({
                                            value: t,
                                            label: `${t} (${t === 'ทั้งหมด' ? data.length : data.filter(d => d.status1 === t).length})`,
                                        }))}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ marginBottom: 4, fontWeight: 'bold', color: '#595959' }}>สถานะ</div>
                                    <Select
                                        value={filterStatus4}
                                        onChange={(v) => { setFilterStatus4(v); setFilterStatus3('ทั้งหมด'); setSelectedRowKeys([]) }}
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="label"
                                        options={[
                                            { value: 'ทั้งหมด', label: `ทั้งหมด (${dataByType.length})` },
                                            ...STATUS4_OPTIONS.map(s => ({
                                                value: s,
                                                label: `${s} (${dataByType.filter(d => d.status4 === s).length})`,
                                            })),
                                        ]}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <div style={{ marginBottom: 4, fontWeight: 'bold', color: '#595959' }}>คอนเฟิร์ม</div>
                                    <Select
                                        value={filterStatus3}
                                        onChange={(v) => { setFilterStatus3(v); setSelectedRowKeys([]) }}
                                        style={{ width: '100%' }}
                                        showSearch
                                        optionFilterProp="label"
                                        options={[
                                            { value: 'ทั้งหมด', label: `ทั้งหมด (${dataByTypeStatus4.length})` },
                                            ...STATUS3_OPTIONS.map(s => ({
                                                value: s,
                                                label: `${s} (${dataByTypeStatus4.filter(d => d.status3 === s).length})`,
                                            })),
                                        ]}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={6} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                    <Button type="primary" icon={<PrinterOutlined />} size="large" onClick={handlePrint}
                                        style={{ background: '#efa420', borderColor: '#efa420', fontWeight: 'bold', boxShadow: '0 2px 6px rgba(239,164,32,0.4)' }}>
                                        พิมพ์รายงานสรุปจดหมาย
                                    </Button>
                                </Col>
                            </Row>
                        </Card>

                        {/* Table */}
                        <Card style={{ borderRadius: 12 }}>
                            <Table size="small" columns={columns}
                                dataSource={displayData.map(d => ({ ...d, key: d._rowKey }))}
                                rowSelection={rowSelection}
                                scroll={{ x: 1300 }}
                                pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200] }}
                                footer={() => `เลือก ${selectedRowKeys.length} รายการ | แสดง ${displayData.length} รายการ (ทั้งหมด ${data.length} รายการ)`}
                            />
                        </Card>
                    </>
                )}
            </Spin>
            <FloatButton.BackTop
                visibilityHeight={200}
                target={() => document.querySelector('.ant-modal-wrap')}
                style={{ bottom: 32, right: 32 }}
                tooltip="กลับขึ้นบน"
            />
        </Modal>
    )
}

export default ReportLetter
